import { resolveArchivedInlineParts } from "../../mail/archivedInlineParts";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { linkedMailService } from "../../../services/linkedMailService";
import { mapItem } from "../../../services/graphMailService";
import type { MailLinkDto } from "../../../services/mailLinkService";
import {
    graphMailService,
    type Email,
    type EmailAddress,
    type MailAttachment,
} from "../../../services/graphMailService";
import { SandboxedMailBody } from "../../mail/SandboxedMailBody";
import { HoverKarte } from "./HoverKarte";
import { DateiTypPlakette } from "../../documents/DateiTypSymbol";
import { mailAusschnitt } from "./hoverVorschau";
import "./vorgangDetail.css";

/** Vorschau einer verknüpften Mail. Inhalt kommt erst beim Hover über die
 * stabile Link-ID. Gesicherte Mails und ihre Inline-Bilder benötigen kein
 * Postfach; nur die Livequelle lädt eine Graph-Anhangsliste nach. */

/**
 * Anhänge je Nachricht — `fetchAttachments` ist, anders als der Body, nicht
 * gecacht. Gedeckelt wie der Blob-Cache der Dokumentvorschau: ein langer
 * Vorgang hielte sonst jede je überfahrene Liste im Speicher.
 */
const ANHANG_CACHE_MAX = 40;
const anhangCache = new Map<string, MailAttachment[]>();

function merkeAnhaenge(messageId: string, liste: MailAttachment[]): void {
    anhangCache.set(messageId, liste);
    while (anhangCache.size > ANHANG_CACHE_MAX) {
        const aeltester = anhangCache.keys().next().value as string;
        anhangCache.delete(aeltester);
    }
}

/** „Klaus Baltes <baltes@…>" — nur die Adresse, wenn es keinen Namen gibt. */
function adresse(a: EmailAddress): string {
    const name = a.name?.trim();
    const addr = a.address?.trim();
    if (name && addr && name.toLowerCase() !== addr.toLowerCase()) return `${name} <${addr}>`;
    return addr || name || "";
}

/**
 * Die Empfänger: aus dem Voll-Body (`to` als Adressen), bis dahin der
 * Anzeige-String des Snapshots. Beides nennt dieselben Adressen; kommt der
 * Voll-Body, ist er die Wahrheit.
 */
function empfaenger(voll: Email | null, link: MailLinkDto): string {
    const ausVoll = (voll?.to ?? []).map(adresse).filter(Boolean);
    if (ausVoll.length) return ausVoll.join(", ");
    return link.recipients ?? "";
}

/** Absender aus dem Snapshot — der steht vor jedem Abruf fest. */
function absender(link: MailLinkDto): string {
    const name = link.senderName?.trim();
    const addr = link.senderEmail?.trim();
    if (name && addr && name.toLowerCase() !== addr.toLowerCase()) return `${name} <${addr}>`;
    return name || addr || "";
}

const Zeile: React.FC<{ label: string; wert?: string | null }> = ({ label, wert }) =>
    wert ? (
        <div className="vgd-hover-vz">
            <dt>{label}</dt>
            <dd title={wert}>{wert}</dd>
        </div>
    ) : null;

/**
 * Der Body-Bereich. Er ist gedeckelt (max-height im CSS) und läuft unten in
 * einen Verlauf aus — aber NUR, wenn wirklich etwas abgeschnitten ist: ein
 * Verlauf über der letzten Zeile einer kurzen Mail sähe aus, als fehlte
 * etwas. Der iframe wächst erst nach dem Laden auf seine Höhe, darum wird
 * nachgemessen, sobald er oder der Bereich sich verändert.
 */
const Koerper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [abgeschnitten, setAbgeschnitten] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const messen = () => setAbgeschnitten(el.scrollHeight > el.clientHeight + 1);
        messen();
        const beobachter = new ResizeObserver(messen);
        beobachter.observe(el);
        for (const kind of Array.from(el.children)) beobachter.observe(kind);
        return () => beobachter.disconnect();
    }, [children]);

    return (
        <div ref={ref} className={`vgd-hover-mailbody${abgeschnitten ? " abgeschnitten" : ""}`}>
            {children}
        </div>
    );
};

const Inhalt: React.FC<{ link: MailLinkDto }> = ({ link }) => {
    const [voll, setVoll] = useState<Email | null>(null);
    const [fehler, setFehler] = useState(false);
    const [anhaenge, setAnhaenge] = useState<MailAttachment[] | null>(
        () => anhangCache.get(String(link.id)) ?? null,
    );

    useEffect(() => {
        let aktiv = true;
        // Erst hier, nicht beim Rendern der Zeile: `inhalt()` läuft erst, wenn
        // die Karte nach der Verweildauer wirklich aufgeht.
        let dispose: (() => void) | undefined;
        linkedMailService.read(link.id).then(async (content) => {
            if (!aktiv) return;
            if (content.source === "unavailable") { setFehler(true); return; }
            if (content.source === "archive") {
                const resolved = await resolveArchivedInlineParts(content.mail.bodyHtml, content.mail.inlineParts ?? [], index => linkedMailService.downloadAttachment(link.id, index));
                if (!aktiv) { resolved.dispose(); return; }
                dispose = resolved.dispose;
                setVoll({ body: resolved.html, to: content.mail.to, hasAttachments: false } as Email);
                setAnhaenge(content.mail.attachments.filter(a => !a.isInline).map(a => ({ name: a.name })) as MailAttachment[]);
            } else {
                setVoll(mapItem(content.mail, content.mail));
            }
        }).catch(() => { if (aktiv) setFehler(true); });
        return () => {
            aktiv = false;
            dispose?.();
        };
    }, [link.id]);

    useEffect(() => {
        if (!voll?.hasAttachments || anhaenge) return;
        let aktiv = true;
        // `voll.id` statt `link.messageId`: der Body kommt mit der aktuellen,
        // ggf. selbst geheilten Id einer verschobenen Mail zurück.
        graphMailService
            .fetchAttachments(link.mailbox, voll.id)
            .then((liste) => {
                // Inline-Bilder (Signaturlogos) sind keine Anhänge, die man
                // nennt — dieselbe Regel wie im Lesefenster.
                const dateien = liste.filter((a) => !a.isInline);
                merkeAnhaenge(String(link.id), dateien);
                if (aktiv) setAnhaenge(dateien);
            })
            .catch(() => {
                // Ohne Anhangsliste fehlt nur die Zeile — kein Grund, die
                // Vorschau zu stören.
                if (aktiv) setAnhaenge([]);
            });
        return () => {
            aktiv = false;
        };
    }, [voll, anhaenge, link.mailbox, link.messageId]);

    const anhangZeile = anhaenge?.length ? anhaenge.map((a) => a.name).join(" · ") : null;
    // Der Snapshot-Anriss ist kurz (Graph liefert höchstens 255 Zeichen); die
    // Kürzung ist nur ein Schutz gegen einen längeren Altbestand.
    const anriss = mailAusschnitt(link.preview, 1200);
    const body = voll?.body?.trim() ?? "";

    return (
        <div className="vgd-hover-info vgd-hover-mail">
            <div className="vgd-hover-vk">Vorschau</div>
            <dl className="vgd-hover-vz-liste">
                <Zeile label="Von" wert={absender(link)} />
                <Zeile label="An" wert={empfaenger(voll, link)} />
                {anhangZeile && (
                    <div className="vgd-hover-vz">
                        <dt>Anhang</dt>
                        {/* Je Anhang sein Dateityp-Symbol (User 22.10.2026). */}
                        <dd title={anhangZeile}>
                            {anhaenge!.map((a, i) => (
                                <React.Fragment key={`${i}-${a.name}`}>
                                    {i > 0 && " · "}
                                    <DateiTypPlakette fileName={a.name} mimeType={a.contentType} className="vgd-hover-dt" />
                                    {a.name}
                                </React.Fragment>
                            ))}
                        </dd>
                    </div>
                )}
            </dl>
            <Koerper>
                {body ? (
                    <SandboxedMailBody html={body} minHeight={80} blockRemote />
                ) : anriss ? (
                    <div className="vgd-hover-mailtext">{anriss}</div>
                ) : voll || fehler ? (
                    <div className="vgd-hover-hinweis">Kein Textanfang gespeichert — zum Lesen öffnen.</div>
                ) : (
                    <div className="vgd-hover-hinweis">Mail wird geladen…</div>
                )}
                {fehler && anriss ? (
                    <div className="vgd-hover-hinweis">
                        Die Mail ist nicht mehr abrufbar — das ist der beim Verknüpfen gespeicherte Anfang.
                    </div>
                ) : null}
            </Koerper>
        </div>
    );
};

export const MailHoverVorschau: React.FC<{
    link: MailLinkDto;
    ankerClass?: string;
    children: React.ReactNode;
}> = ({ link, ankerClass, children }) => (
    <HoverKarte breite={520} ankerClass={ankerClass} inhalt={() => <Inhalt link={link} />}>
        {children}
    </HoverKarte>
);
